import type {
  ApprovalRequest,
  CampusNode,
  LaunchCheck,
  Permission,
  RoleRecord,
  Scope,
  StaffMember,
  Tenant,
} from '../types/governance'

/**
 * Why an action was refused. The interface shows the actual reason rather than
 * a generic "not allowed", because an administrator needs to know whether they
 * lack the permission, are outside their scope, or need someone to approve.
 */
export type DenialReason =
  | 'no_permission'
  | 'out_of_scope'
  | 'access_expired'
  | 'account_not_active'
  | 'awaiting_approval'
  | 'requires_step_up'

export interface Decision {
  allowed: boolean
  reason: DenialReason | null
  /** The assignment that granted it, so the console can show provenance. */
  viaRole: string | null
  message: string
}

const MESSAGES: Record<DenialReason, string> = {
  no_permission: 'None of your roles carry this permission.',
  out_of_scope: 'You hold this permission, but not for this tenant, institution or campus.',
  access_expired: 'This role assignment has expired. Access stopped automatically.',
  account_not_active: 'Your administrative account is not active.',
  awaiting_approval: 'This role assignment is waiting on a second approver before it activates.',
  requires_step_up: 'This action needs step-up authentication before it can proceed.',
}

/** True when `held` covers `target`. A wider scope covers a narrower one. */
export function scopeCovers(held: Scope, target: Scope): boolean {
  if (held.level === 'global') return true
  if (held.tenantId && held.tenantId !== target.tenantId) return false
  if (held.level === 'tenant') return true
  if (held.institutionId && held.institutionId !== target.institutionId) return false
  if (held.level === 'institution') return true
  if (held.campusId && held.campusId !== target.campusId) return false
  if (held.level === 'campus') return true
  if (held.facultyId && held.facultyId !== target.facultyId) return false
  return true
}

export function describeScope(scope: Scope, tenants: Tenant[]): string {
  if (scope.level === 'global') return 'Campuzon global — all tenants'
  const tenant = tenants.find((item) => item.id === scope.tenantId)
  const institution = tenant?.institutions.find((item) => item.id === scope.institutionId)
  const campus = institution?.campuses.find((item) => item.id === scope.campusId)
  const parts = [tenant?.name, institution?.name, campus?.name].filter(Boolean)
  const base = parts.length > 0 ? parts.join(' · ') : 'Unresolved scope'
  return scope.domain ? `${base} · ${scope.domain} only` : base
}

function assignmentLive(expiresAt: string | null, now: Date): boolean {
  if (!expiresAt) return true
  return new Date(expiresAt).getTime() > now.getTime()
}

/**
 * The single authorization decision. Every administrative surface asks this
 * rather than checking a role name, so a role rename can never widen access.
 *
 * In production this same evaluation runs server-side and is authoritative;
 * the console runs it only to decide what to show and to explain refusals.
 */
export function can(
  staff: StaffMember,
  roles: RoleRecord[],
  permission: Permission,
  target: Scope,
  now: Date = new Date(),
): Decision {
  if (staff.status !== 'active') {
    return {
      allowed: false,
      reason: 'account_not_active',
      viaRole: null,
      message: MESSAGES.account_not_active,
    }
  }

  let sawPermission = false
  let sawExpired = false
  let sawPending = false

  for (const assignment of staff.assignments) {
    const role = roles.find((item) => item.id === assignment.roleId)
    if (!role || !role.permissions.includes(permission)) continue
    sawPermission = true

    if (assignment.state === 'revoked') continue
    if (assignment.state === 'awaiting_approval') {
      sawPending = true
      continue
    }
    if (!assignmentLive(assignment.expiresAt, now)) {
      sawExpired = true
      continue
    }
    if (!scopeCovers(assignment.scope, target)) continue
    if (assignment.scope.domain && !permission.startsWith(assignment.scope.domain)) continue

    return { allowed: true, reason: null, viaRole: role.name, message: 'Permitted.' }
  }

  const reason: DenialReason = !sawPermission
    ? 'no_permission'
    : sawPending
      ? 'awaiting_approval'
      : sawExpired
        ? 'access_expired'
        : 'out_of_scope'

  return { allowed: false, reason, viaRole: null, message: MESSAGES[reason] }
}

/** Every permission this person effectively holds, for the access review. */
export function effectivePermissions(
  staff: StaffMember,
  roles: RoleRecord[],
  now: Date = new Date(),
): Permission[] {
  const held = new Set<Permission>()
  for (const assignment of staff.assignments) {
    if (assignment.state !== 'active') continue
    if (!assignmentLive(assignment.expiresAt, now)) continue
    const role = roles.find((item) => item.id === assignment.roleId)
    role?.permissions.forEach((permission) => held.add(permission))
  }
  return [...held].sort()
}

/**
 * Separation of duties. A person may never approve their own request, and must
 * hold the approving permission in a scope that covers the request.
 */
export function canApprove(
  staff: StaffMember,
  roles: RoleRecord[],
  request: ApprovalRequest,
): Decision {
  if (request.requestedBy === staff.handle) {
    return {
      allowed: false,
      reason: 'no_permission',
      viaRole: null,
      message: 'You raised this request. A different authorised person must approve it.',
    }
  }
  return can(staff, roles, request.permissionToApprove, request.scope)
}

/* ------------------------------------------------------------ launch engine */

export interface LaunchOutcome {
  campus: CampusNode
  blockers: LaunchCheck[]
  warnings: LaunchCheck[]
  info: LaunchCheck[]
  satisfied: number
  total: number
  /** A campus cannot be activated while any blocker is outstanding. */
  canGoLive: boolean
}

/** Derived from the checks, never asserted by a stored "ready" flag. */
export function evaluateLaunch(campus: CampusNode, checks: LaunchCheck[]): LaunchOutcome {
  const outstanding = checks.filter((check) => !check.satisfiedFor.includes(campus.id))
  const blockers = outstanding.filter((check) => check.severity === 'blocker')
  return {
    campus,
    blockers,
    warnings: outstanding.filter((check) => check.severity === 'warning'),
    info: outstanding.filter((check) => check.severity === 'info'),
    satisfied: checks.length - outstanding.length,
    total: checks.length,
    canGoLive: blockers.length === 0,
  }
}

export function allCampuses(tenants: Tenant[]): {
  campus: CampusNode
  tenant: Tenant
  institution: string
}[] {
  return tenants.flatMap((tenant) =>
    tenant.institutions.flatMap((institution) =>
      institution.campuses.map((campus) => ({
        campus,
        tenant,
        institution: institution.name,
      })),
    ),
  )
}
